import { useIssueNumber } from "@/hooks/useIssueNumber";
import { NOTIFY_ON_READY } from "@/lib/featureFlags";
import PendingStatusStrip from "./PendingStatusStrip";
import { ResultsTopNav } from "./ResultsTopNav";

interface Props {
  hostname: string | null;
  stage?: "fetching" | "analysing";
  outfitId: string | null;
  isGuest: boolean;
  onBack: () => void;
}

/**
 * Optimistic loading shell for the URL paste flow. Mounts immediately on
 * `onSubmitted`, before the wrapper edge fn has downloaded the og:image.
 * Shares the standard ResultsTopNav so the transition into the full Results
 * view is seamless once `image_url` populates.
 */
export default function UrlPendingResults({
  hostname,
  stage = "fetching",
  outfitId,
  isGuest,
  onBack,
}: Props) {
  const issueNumber = useIssueNumber(outfitId, isGuest);
  const isAnalysing = stage === "analysing";
  const headline = isAnalysing
    ? "Analysing your look…"
    : `Fetching from ${hostname ?? "the web"}…`;
  const subline = isAnalysing
    ? "Spotting pieces and matching styles. Almost there."
    : "Pulling the look from the page.";
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#FAF6EF",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <style>{`
        @keyframes l2f-url-pending-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.55; }
        }
        @keyframes l2f-url-pending-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      <ResultsTopNav
        issueNumber={issueNumber}
        isSaved={false}
        onBack={onBack}
        onBookmarkTap={onBack}
      />
      <PendingStatusStrip
        status="analysing"
        notifyEnabled={NOTIFY_ON_READY}
      />
      <div style={{ padding: "16px 16px 96px" }}>
        <div
          style={{
            width: "100%",
            aspectRatio: "3 / 4",
            background: "#FFFCF8",
            border: "0.5px solid #E8E2D9",
            borderRadius: 20,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 14,
            padding: 24,
            textAlign: "center",
            animation: "l2f-url-pending-pulse 1.6s ease-in-out infinite",
          }}
        >
          <i
            className="ti ti-loader-2"
            style={{
              fontSize: 28,
              color: "#C84B2F",
              opacity: 0.7,
              animation: "l2f-url-pending-spin 1s linear infinite",
            }}
          />
          <div
            style={{
              fontSize: 19,
              fontStyle: "italic",
              fontFamily:
                "var(--font-serif), 'Instrument Serif', Georgia, serif",
              color: "#2C2820",
              lineHeight: 1.2,
            }}
          >
            {headline}
          </div>
          <div
            className="l2f-type-caption"
            style={{
              color: "#6B6560",
              maxWidth: 240,
              lineHeight: 1.5,
            }}
          >
            {subline}
          </div>
        </div>
      </div>
    </div>
  );
}
